/**
 * Brand-accurate marketplace logos for the home "Shop With Us" section.
 *
 *  • Amazon — FontAwesome (`fa-brands fa-amazon`) rendered in #FF9900.
 *    SimpleIcons removed the Amazon glyph for trademark reasons.
 *  • TikTok — official SimpleIcons SVG path rendered with the iconic
 *    red+cyan offset (TikTok Shop uses this same wordmark/glyph).
 *  • eBay — official SimpleIcons SVG path rendered in eBay primary red #E53238.
 *
 * SimpleIcons paths are CC0-licensed and are the same SVGs used by
 * Wikipedia / Stripe / Vercel / etc. — fully brand-accurate at any size.
 */
import React from "react";
// Some brand glyphs are stripped from `simple-icons` due to trademark
// policy. We import them safely so a missing entry never crashes the app.
import * as si from "simple-icons";

const SIMPLE = {
  tiktok: si.siTiktok,
  ebay: si.siEbay,
};

/** Render a SimpleIcons SVG path with brand colour + accessible label. */
function SimpleIcon({ icon, size = 32, color }) {
  if (!icon) return null;
  return (
    <svg
      role="img"
      aria-label={icon.title}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={color || `#${icon.hex}`}
    >
      <title>{icon.title}</title>
      <path d={icon.path} />
    </svg>
  );
}

/** Render the TikTok glyph with its iconic red+cyan offset look. */
function TikTokGlyph({ size = 32 }) {
  const icon = SIMPLE.tiktok;
  if (!icon) return null;
  return (
    <svg
      role="img"
      aria-label="TikTok Shop"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <title>TikTok Shop</title>
      {/* Cyan layer offset to the right */}
      <path d={icon.path} fill="#25F4EE" transform="translate(0.7, 0)" />
      {/* Red layer offset to the left */}
      <path d={icon.path} fill="#FE2C55" transform="translate(-0.7, 0)" />
      {/* Solid black core on top */}
      <path d={icon.path} fill="#000000" />
    </svg>
  );
}

/** Dispatcher used by the marketplace cards on the home page. */
export function BrandLogo({ name, size = 32 }) {
  const key = (name || "").toLowerCase().replace(/\s+/g, "");
  if (key.startsWith("amazon")) {
    // FontAwesome Amazon swoosh in official Amazon orange
    return <i className="fa-brands fa-amazon" style={{ color: "#FF9900", fontSize: `${size}px`, lineHeight: 1 }} aria-label="Amazon" />;
  }
  if (key.startsWith("tiktok")) {
    return <TikTokGlyph size={size} />;
  }
  if (key.startsWith("ebay")) {
    return <SimpleIcon icon={SIMPLE.ebay} size={size} color="#E53238" />;
  }
  if (key.startsWith("crown01") || key.startsWith("crown")) {
    // Own-brand mark — gold "C" on black, matching the header logo
    return (
      <div
        aria-label="Crown01"
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          background: "var(--c-black)",
          color: "var(--c-gold)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Playfair Display', serif",
          fontWeight: 700,
          fontSize: size * 0.72,
          lineHeight: 1,
        }}
      >
        C
      </div>
    );
  }
  return null;
}
